'use client'

import { MapContainer, TileLayer, Popup } from 'react-leaflet'
import { Marker } from '@adamscybot/react-leaflet-component-marker'
import { RawStoreData } from '@/lib/stores/store-types'
import { useEffect, useRef } from 'react'
import { Map as LeafletMap, Marker as LeafletMarker } from 'leaflet'
import { StoreMarkerIcon } from './StoreMarkerIcon'
import { formatPhoneNumber } from '@/lib/i18n/format-phone-number'
import StoreDetailHours from './StoreDetailHours'
import { isStoreVeryClosed } from '@/lib/utils'

function StoreDetailHoursOnMap({
	store,
	locale,
	dictionary,
	currentTime,
}: {
	store: RawStoreData
	locale: string
	dictionary: Record<string, string>
	currentTime: Date
}) {
	const isVeryClosed = isStoreVeryClosed(store)
	return (
		<div>
			{isVeryClosed && store.openInfo === 'CLOSED_PERMANENTLY' && (
				<div className="text-red-600 text-sm">{dictionary['permanently-closed']}</div>
			)}
			{isVeryClosed && store.openInfo === 'CLOSED_TEMPORARILY' && (
				<div className="text-orange-600 text-sm">{dictionary['temporarily-closed']}</div>
			)}
			{!isVeryClosed && (
				<StoreDetailHours locale={locale} store={store} dictionary={dictionary} currentTime={currentTime} />
			)}
		</div>
	)
}

export default function StoreMap({
	stores,
	originalCenter,
	zoom,
	storeToHighlight,
	dictionary,
	currentTime,
	locale,
}: {
	stores: RawStoreData[]
	originalCenter: { latitude: number; longitude: number }
	zoom: number
	storeToHighlight: RawStoreData | null
	dictionary: Record<string, string>
	currentTime: Date
	locale: string
}) {
	const map = useRef<LeafletMap>(null)
	const markerRef = useRef<LeafletMarker>(null)

	useEffect(() => {
		if (storeToHighlight) {
			map.current?.flyTo([storeToHighlight.coordinates.latitude, storeToHighlight.coordinates.longitude], zoom)
			if (markerRef.current) {
				markerRef.current.openPopup()
			}
		}

		if (originalCenter) {
			map.current?.flyTo([originalCenter.latitude, originalCenter.longitude], zoom)
		}
	}, [originalCenter, storeToHighlight, zoom])

	return (
		<MapContainer
			center={[originalCenter.latitude, originalCenter.longitude]}
			zoom={zoom}
			scrollWheelZoom={true}
			style={{ height: '100%', width: '100%' }}
			ref={map}
			attributionControl={true}
		>
			{stores.map((store) => (
				<Marker
					ref={storeToHighlight?.id === store.id ? markerRef : null}
					key={store.id}
					position={[store.coordinates.latitude ?? 0, store.coordinates.longitude ?? 0]}
					icon={<StoreMarkerIcon storeType={store.store_type} size={36} />}
					zIndexOffset={store.store_type === 'Brand House' ? 1000 : store.store_type === 'Factory House' ? 500 : 0}
				>
					<Popup>
						<div className="flex flex-col gap-2">
							<h2 className="text-lg font-bold leading-tight">{store.name}</h2>
							<div className="leading-snug">
								<div>{store.address.street}</div>
								<div>
									{store.address.city}, {store.address.state} {store.address.zip}
								</div>
								{store.phone && (
									<a href={`tel:${store.phone}`}>{formatPhoneNumber(store.address.country, store.phone)}</a>
								)}
							</div>

							{['Brand House', 'Factory House'].includes(store.store_type) && (
								<StoreDetailHoursOnMap
									store={store}
									locale={locale}
									dictionary={dictionary}
									currentTime={currentTime}
								/>
							)}
						</div>
					</Popup>
				</Marker>
			))}
			<TileLayer
				attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
				url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
			/>
		</MapContainer>
	)
}
